import { useMutation, useQuery } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import {
  DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION,
  GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS,
  START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION,
} from '../../../queries/mutations';
import {
  clearStoredMicrosoftGraphAuthorizationResult,
  clearStoredMicrosoftGraphAuthorizationState,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY,
  writeStoredMicrosoftGraphAuthorizationState,
} from '../lib/microsoft-graph-authorization';

export interface OutlookConnectController {
  // State
  outlookConnectEnabled: boolean;

  /** True when Outlook is enabled but auth has expired and interactive login is required. */
  outlookNeedsReauth: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;

  /**
   * Attempt interactive login if Outlook is enabled but auth has expired.
   * Must be called from a user gesture (click handler) so the popup is not blocked.
   * Returns true if a login popup was triggered.
   */
  reconnectIfNeeded(): Promise<boolean>;
}

export function useOutlookConnectController({
  canConnectOutlook,
  notify,
  serverSettings,
  onPersist,
}: {
  canConnectOutlook: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
}): OutlookConnectController {
  const [startNovaMicrosoftGraphAuthorization] = useMutation(START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION);
  const [disconnectNovaMicrosoftGraphConnection] = useMutation(DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION);

  // Initialize from server settings if available, default to false
  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(
    () => canConnectOutlook && (serverSettings?.outlookConnectEnabled ?? false),
  );
  const [outlookNeedsReauth, setOutlookNeedsReauth] = useState(false);
  const { data: delegatedConnectionStatusData, refetch: refetchDelegatedConnectionStatus } = useQuery(
    GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS,
    {
      fetchPolicy: 'cache-and-network',
      // Nova can mount on the standalone chat route or as the global overlay.
      // Only resolve saved delegated connection health when Outlook Connect is
      // enabled, so page load with the toggle off does not validate the new BFF
      // GraphQL field.
      skip: !canConnectOutlook || !outlookConnectEnabled,
    },
  );
  const delegatedConnectionStatus = delegatedConnectionStatusData?.getNovaMicrosoftGraphConnectionStatus;

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setOutlookConnectEnabledState(canConnectOutlook && serverSettings.outlookConnectEnabled);
    }
  }, [canConnectOutlook, serverSettings]);

  useEffect(() => {
    setOutlookNeedsReauth(outlookConnectEnabled && (delegatedConnectionStatus?.needsReauth ?? false));
  }, [delegatedConnectionStatus?.needsReauth, outlookConnectEnabled]);

  // Users who cannot connect Outlook (e.g. WTW colleagues) must never have it on:
  // reset local state and persist the disable when a stale "on" setting arrives.
  useEffect(() => {
    if (canConnectOutlook) {
      return;
    }

    setOutlookNeedsReauth(false);

    if (outlookConnectEnabled) {
      setOutlookConnectEnabledState(false);
      onPersist?.('outlookConnectEnabled', false);
    }
  }, [canConnectOutlook, outlookConnectEnabled, onPersist]);

  const delegatedAuthorizationCleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      delegatedAuthorizationCleanupRef.current?.();
    },
    [],
  );

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    setOutlookConnectEnabledState(false);
    setOutlookNeedsReauth(false);
    onPersist?.('outlookConnectEnabled', false);

    // If the user turns the toggle off while an OAuth popup is still in
    // flight, stop listening for that stale callback before revoking the
    // connection. Otherwise a late callback can update the UI after the user
    // explicitly disabled Outlook Connect.
    delegatedAuthorizationCleanupRef.current?.();
    clearStoredMicrosoftGraphAuthorizationResult();
    clearStoredMicrosoftGraphAuthorizationState();

    disconnectNovaMicrosoftGraphConnection()
      .then(({ data }) => {
        browserLogger.info('[OutlookConnect] Delegated MS Graph connection disconnected', {
          connectionUuid: data?.disconnectNovaMicrosoftGraphConnection.connectionUuid,
          status: data?.disconnectNovaMicrosoftGraphConnection.status,
        });
        void refetchDelegatedConnectionStatus();
      })
      .catch((error) => {
        browserLogger.error('[OutlookConnect] Failed to disconnect delegated MS Graph connection', { error });
      });
  }, [disconnectNovaMicrosoftGraphConnection, refetchDelegatedConnectionStatus, onPersist]);

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    if (!canConnectOutlook) {
      return;
    }

    setOutlookConnectEnabledState(true);
    onPersist?.('outlookConnectEnabled', true);

    // The callback page communicates completion through localStorage because
    // popup postMessage can be missed if the opener reloads or the popup is
    // closed immediately after returning from Microsoft. Clear any stale
    // result before starting so a previous failed attempt cannot disconnect
    // the fresh pending connection.
    clearStoredMicrosoftGraphAuthorizationResult();
    clearStoredMicrosoftGraphAuthorizationState();

    // Open a blank popup synchronously from the user's click. Waiting until
    // after the start mutation returns would make browsers treat it as a
    // non-user-initiated popup and block it.
    const popup = window.open(
      '',
      'nova-microsoft-graph-authorization',
      NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
    );

    if (!popup) {
      disableOutlook();
      notify.error('Outlook authentication popup was blocked. Please allow popups and try again.');
      return;
    }

    try {
      let completed = false;
      let loggedPopupClosed = false;
      const authorizationTimeoutRef: { current?: number } = {};
      let cleanup: () => void = () => {};
      const completeAuthorization = (status: 'success' | 'error') => {
        // Completion can arrive through both postMessage and localStorage
        // polling. Keep the handler idempotent so a successful callback does
        // not double-toast or race with cleanup.
        if (completed) {
          return;
        }

        completed = true;
        cleanup();

        if (status === 'success') {
          setOutlookNeedsReauth(false);
          void refetchDelegatedConnectionStatus();
          notify.success('Outlook connected successfully');
        } else {
          disableOutlook();
          notify.error('Outlook authentication failed: Please try again.');
        }
      };
      const pollInterval = window.setInterval(() => {
        const storedResult = localStorage.getItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
        if (storedResult) {
          localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
          try {
            completeAuthorization(JSON.parse(storedResult).status === 'success' ? 'success' : 'error');
          } catch {
            completeAuthorization('error');
          }
          return;
        }

        // Some browsers report popup.closed unreliably once the popup has
        // navigated to login.microsoftonline.com. Treat the callback result
        // as the source of truth; using popup.closed as cancellation can
        // disconnect the backend connection while the user is still signing in.
        if (!completed && popup.closed && !loggedPopupClosed) {
          loggedPopupClosed = true;
          browserLogger.info('[OutlookConnect] Delegated MS Graph authorization popup appears closed');
        }
      }, 500);
      const handleAuthorizationMessage = (event: MessageEvent) => {
        if (
          event.origin !== window.location.origin ||
          event.data?.type !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE
        ) {
          return;
        }

        completeAuthorization(event.data.status === 'success' ? 'success' : 'error');
      };
      const handleAuthorizationStorage = (event: StorageEvent) => {
        if (event.key !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY || !event.newValue) {
          return;
        }

        localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
        try {
          completeAuthorization(JSON.parse(event.newValue).status === 'success' ? 'success' : 'error');
        } catch {
          completeAuthorization('error');
        }
      };
      cleanup = () => {
        window.clearInterval(pollInterval);
        if (authorizationTimeoutRef.current != null) {
          window.clearTimeout(authorizationTimeoutRef.current);
        }
        window.removeEventListener('message', handleAuthorizationMessage);
        window.removeEventListener('storage', handleAuthorizationStorage);
        delegatedAuthorizationCleanupRef.current = null;
      };

      delegatedAuthorizationCleanupRef.current?.();
      delegatedAuthorizationCleanupRef.current = cleanup;
      window.addEventListener('message', handleAuthorizationMessage);
      window.addEventListener('storage', handleAuthorizationStorage);

      browserLogger.info('[OutlookConnect] Starting delegated MS Graph authorization');
      const { data } = await startNovaMicrosoftGraphAuthorization({
        variables: {
          command: {
            // users-service stores this exact redirect URI with the pending
            // connection and validates it during callback completion. Build
            // it from the current origin so local, stage, and prod each use
            // the host that initiated the popup.
            redirectUri: `${window.location.origin}/outlook-connections/oauth/callback`,
          },
        },
      });
      const authorization = data?.startNovaMicrosoftGraphAuthorization;

      if (!authorization?.authorizationUrl) {
        throw new Error('Unable to start Microsoft authorization');
      }

      // If the user abandons the popup and no callback ever arrives, fail the
      // optimistic toggle once the backend's pending authorization state has
      // expired. This avoids relying on popup.closed while still preventing an
      // indefinitely enabled UI with no usable connection.
      const authorizationExpiresAt = new Date(authorization.expiresAt).getTime();
      const authorizationTimeoutMs = Math.max(0, authorizationExpiresAt - Date.now()) + 5000;
      authorizationTimeoutRef.current = window.setTimeout(() => {
        completeAuthorization('error');
      }, authorizationTimeoutMs);

      writeStoredMicrosoftGraphAuthorizationState({
        connectionUuid: authorization.connectionUuid,
        expiresAt: authorization.expiresAt,
        // The backend-generated state is checked both here and by
        // users-service. Keeping it in browser storage lets the callback
        // page reject stray or replayed Microsoft redirects.
        state: authorization.state,
      });

      browserLogger.info('[OutlookConnect] Delegated MS Graph authorization started', {
        connectionUuid: authorization.connectionUuid,
        expiresAt: authorization.expiresAt,
      });
      popup.location.assign(authorization.authorizationUrl);
    } catch (error) {
      browserLogger.error('[OutlookConnect] Failed to start delegated MS Graph authorization', { error });
      delegatedAuthorizationCleanupRef.current?.();
      popup.close();
      disableOutlook();
      notify.error('Outlook authentication failed: Please try again.');
    }
  }, [
    canConnectOutlook,
    startNovaMicrosoftGraphAuthorization,
    refetchDelegatedConnectionStatus,
    disableOutlook,
    notify,
    onPersist,
  ]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  // Restart the delegated authorization flow if Outlook is enabled but auth has expired.
  // Called from click handlers (user gesture) so the popup is allowed by the browser.
  const reconnectIfNeeded = useCallback(async (): Promise<boolean> => {
    if (!canConnectOutlook || !outlookConnectEnabled || !outlookNeedsReauth) return false;

    await enableOutlook();
    return true;
  }, [canConnectOutlook, outlookConnectEnabled, outlookNeedsReauth, enableOutlook]);

  return useMemo(
    () => ({
      outlookConnectEnabled,
      outlookNeedsReauth,
      setOutlookConnectEnabled,
      reconnectIfNeeded,
    }),
    [outlookConnectEnabled, outlookNeedsReauth, setOutlookConnectEnabled, reconnectIfNeeded],
  );
}
